import RichMessageEditor from '@/components/ads/RichMessageEditor';
import RichMessagePreview from '@/components/ads/RichMessagePreview';
import { messageFallback, stickers } from '@/constants/richMessage';
import type { RichTextItem } from '@/types/ads';
import { useBranding } from '@/context/BrandingContext';
import LocationTargetSelector from '@/components/ads/LocationTargetSelector';
import CampaignSegmentationSummary from '@/components/audience/CampaignSegmentationSummary';
import CampaignAudienceFields from '@/components/audience/CampaignAudienceFields';
import LocationToggle from '@/components/ads/LocationToggle';
import LocationHelpLabel from '@/components/ads/LocationHelpLabel';
import HomeCampaignFields from '@/components/ads/HomeCampaignFields';
import { defaultHomeSettings } from '@/constants/homeCampaign';
import { useTranslation } from '@/hooks/useTranslation';
import RadioCampaignFields from '@/components/ads/RadioCampaignFields';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import Input from '@/components/ui/Input';
import ImageUrlUploadField from '@/components/ui/ImageUrlUploadField';
import Select from '@/components/ui/Select';
import Toggle from '@/components/ui/Toggle';
import Button from '@/components/ui/Button';
import MultiSelect, { MultiSelectOption } from '@/components/ui/MultiSelect';
import { CampaignTypeCards } from '@/components/ads';
import {
  AdCampaign, AdCreative,
  AdCampaignDurationMode,
  AdCampaignStatus, AdCampaignType,
  AdCampaignSchedule,
  AdScheduleDayOfWeek,
  BannerOverlayPosition,
  CreateAdCampaignRequest,
  NewsTickerSourceType,
} from '@/types/ads';
import { useAdCampaign, useCreateAdCampaign, useUpdateAdCampaign } from '@/hooks/useAdCampaigns';
import { useTVChannelsForDropdown } from '@/hooks/useTVChannel';
import { TVChannel } from '@/types/model';
import { apiClient, ApiClientError } from '@/services/apiClient';
import { MediaSource } from '@/types/media';
import {
  TEXT_AD_DEFAULT_FONT,
  TEXT_AD_FONT_OPTIONS,
  resolveTextAdFontOption,
} from '@/constants/textAdFonts';

type WizardMode = 'create' | 'edit';
type ChannelMode = 'ALL' | 'INCLUDE' | 'EXCLUDE';

const pad2 = (n: number) => String(n).padStart(2, '0');

const toLocalDateTimeInput = (iso?: string) => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}T${pad2(date.getHours())}:${pad2(
    date.getMinutes()
  )}`;
};

const toIsoFromLocalDateTimeInput = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toISOString();
};

const scheduleDayOptions: Array<{ value: AdScheduleDayOfWeek; label: string; shortLabel: string }> = [
  { value: 'MONDAY', label: 'Lunes', shortLabel: 'L' },
  { value: 'TUESDAY', label: 'Martes', shortLabel: 'M' },
  { value: 'WEDNESDAY', label: 'Miércoles', shortLabel: 'X' },
  { value: 'THURSDAY', label: 'Jueves', shortLabel: 'J' },
  { value: 'FRIDAY', label: 'Viernes', shortLabel: 'V' },
  { value: 'SATURDAY', label: 'Sábado', shortLabel: 'S' },
  { value: 'SUNDAY', label: 'Domingo', shortLabel: 'D' },
];

const normalizeTimeInput = (value?: string) => {
  if (!value) return '';
  return value.length === 5 ? `${value}:00` : value.slice(0, 8);
};

const toApiLocalTime = (value?: string) => {
  if (!value) return value;
  return value.length === 5 ? `${value}:00` : value;
};

const emptySchedule = (): AdCampaignSchedule => ({
  daysOfWeek: [],
  triggerTime: '',
});

const campaignSchema = z
  .object({
    name: z.string().min(1, 'El nombre es requerido').max(255, 'Máximo 255 caracteres'),
    type: z.enum(['VIDEO', 'BANNER', 'TEXT', 'NEWS_TICKER']).optional(),
    durationMode: z.enum(['DATE_RANGE', 'UNTIL_DEACTIVATED']),
    startAt: z.string().optional(),
    endAt: z.string().optional(),
    priority: z.number().int('La prioridad debe ser un número entero').min(0, 'La prioridad debe ser >= 0'),
    weight: z.number().int('El peso debe ser un número entero').min(1, 'El peso debe ser >= 1'),
    version: z.number().min(0),
    includeChannelIds: z.array(z.string()),
    excludeChannelIds: z.array(z.string()),
    audience: z.object({
      includeNodeIds: z.array(z.string()).optional(), excludeNodeIds: z.array(z.string()).optional(),
    }).optional(),
    locationTargets: z.array(z.object({
      excluded: z.boolean().optional(),
      label: z.string().optional(),
      countryId: z.string().min(1),
      adminAreaId: z.string().optional(),
      localityId: z.string().optional(),
    })).optional(),
    schedules: z.array(z.object({
      daysOfWeek: z.array(z.enum(['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'])).min(1, 'Selecciona al menos un día'),
      triggerTime: z.string().min(1, 'La hora es requerida'),
    })).optional(),
    maxImpressionsPerUserPerDay: z.number().min(0, 'Debe ser >= 0').nullable(),
    videoMode: z.enum(['NONE', 'VAST', 'VMAP']),
    videoPlacements: z.array(z.literal('PRE_ROLL')).optional(),
    adTagUrl: z.string().optional(),
    videoUrl: z.string().optional(),
    videoDismissible: z.boolean().optional(),
    videoMinSecondsBeforeDismiss: z.number().min(1, 'Debe ser >= 1').max(120, 'Debe ser <= 120').optional(),
    // text property required if type is TEXT
    text: z
      .string()
      .max(500, 'Máximo 500 caracteres')
      .optional()
      .transform((value) => (value === '' ? undefined : value)),
    textItems: z.array(z.object({
      style: z.object({
        fontFamily: z.string().optional(), fontSize: z.number().min(10).max(200).optional(),
        color: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
        bold: z.boolean().optional(), italic: z.boolean().optional(), underline: z.boolean().optional(),
      }).optional(),
      kind: z.enum(['TEXT', 'IMAGE', 'EMOJI', 'STICKER']),
      text: z.string().optional(), imageUrl: z.string().optional(), alt: z.string().optional(),
      sticker: z.string().optional(),
    })).max(24).optional(),
    textFontSize: z.number().min(10, 'El tamaño de fuente debe ser >= 10').max(200, 'El tamaño de fuente debe ser <= 200').optional(),
    textFontColor: z
      .string()
      .regex(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, 'El color de fuente debe ser hex válido')
      .optional(),
    textFontFamily: z.string().optional(),
    textBackgroundColor: z
      .string()
      .regex(/^(transparent|#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}))$/, 'El color de fondo debe ser hex válido')
      .optional(),
    textTickerSpeedSeconds: z
      .number()
      .min(4, 'La velocidad debe ser >= 4s')
      .max(60, 'La velocidad debe ser <= 60s')
      .optional(),
    newsTickerSourceType: z.enum(['MANUAL', 'RSS', 'MANUAL_AND_RSS']).optional(),
    newsTickerRssUrl: z.string().optional(),
    newsTickerMaxItems: z.number().min(1, 'Debe ser >= 1').max(30, 'Debe ser <= 30').optional(),
    newsTickerBackgroundImageUrl: z.string().optional(),
    newsTickerLeftImageUrl: z.string().optional(),
    newsTickerSeparator: z.string().max(50, 'Máximo 50 caracteres').optional(),
    newsTickerLoopEnabled: z.boolean().optional(),
    newsTickerItemsText: z.string().optional(),
    bannerOverlayEnabled: z.boolean(),
    showOnLiveTv: z.boolean(),
    home: z.object({ enabled: z.boolean(), placement: z.enum(['HOME_SLIDE', 'HOME_TICKER']), mediaUrl: z.string(), durationSeconds: z.number(), muted: z.boolean(), everyNItems: z.number(), allowKids: z.boolean(), allowSports: z.boolean() }),
    showOnRadio: z.boolean(),
    radioDisplayMode: z.enum(['BOTH', 'BROWSING', 'FULLSCREEN']),
    includeRadioStationIds: z.array(z.string()),
    excludeRadioStationIds: z.array(z.string()),
    showOnVod: z.boolean().optional(),
    bannerPosition: z.enum(['TOP', 'BOTTOM']),
    bannerImageUrl: z.string().optional(),
    bannerScaleFactor: z.number().min(0.3, 'Escala mínima 0.3').max(50, 'Escala máxima 50').optional(),
    showTimeInSeconds: z.number().min(0, 'Debe ser >= 0'),
  })
  .superRefine((data, ctx) => {
    if (data.home.enabled && (data.type === 'VIDEO' || data.type === 'BANNER') && !/^https?:\/\/[^\s/]+/i.test((data.type === 'VIDEO' ? data.videoUrl : data.bannerImageUrl) ?? '')) {
      ctx.addIssue({ code: 'custom', message: 'homeMedia', path: ['home', 'mediaUrl'] });
    }
    if (data.home.enabled && (data.type === 'VIDEO' || data.type === 'BANNER') &&
        (data.home.durationSeconds < 5 || data.home.durationSeconds > 60 || data.home.everyNItems < 1 || data.home.everyNItems > 10)) {
      ctx.addIssue({ code: 'custom', message: 'homeSettings', path: ['home'] });
    }
    if (data.durationMode === 'DATE_RANGE') {
      if (!data.startAt?.trim()) {
        ctx.addIssue({
          code: 'custom',
          message: 'La fecha de inicio es requerida',
          path: ['startAt'],
        });
      }
      if (!data.endAt?.trim()) {
        ctx.addIssue({
          code: 'custom',
          message: 'La fecha de fin es requerida',
          path: ['endAt'],
        });
      }

      const start = new Date(data.startAt ?? '');
      const end = new Date(data.endAt ?? '');
      if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime()) && start >= end) {
        ctx.addIssue({
          code: 'custom',
          message: 'La fecha de inicio debe ser anterior a la de fin',
          path: ['endAt'],
        });
      }
    }

    if (data.videoMode !== 'NONE') {
      const url = data.adTagUrl?.trim();
      if (!url) {
        ctx.addIssue({ code: 'custom', message: 'adTagUrl es requerido para VAST/VMAP', path: ['adTagUrl'] });
      } else {
        try {
          new URL(url);
        } catch {
          ctx.addIssue({ code: 'custom', message: 'adTagUrl debe ser una URL válida', path: ['adTagUrl'] });
        }
      }
    }

    if (!data.showOnLiveTv && !data.showOnVod && !data.showOnRadio && !data.home.enabled) {
      ctx.addIssue({ code: 'custom', message: 'Selecciona al menos una ubicación', path: ['showOnVod'] });
    }
    if ((data.showOnLiveTv || data.showOnVod || data.showOnRadio) && (data.type === 'VIDEO' || data.type === 'BANNER')) {
      const url = data.type === 'VIDEO' ? data.videoUrl : data.bannerImageUrl;
      if (!url || !/^https?:\/\/[^\s/]+/i.test(url)) ctx.addIssue({ code: 'custom', message: 'Selecciona un archivo válido', path: [data.type === 'VIDEO' ? 'videoUrl' : 'bannerImageUrl'] });
    }
    if (data.showOnRadio && data.type === 'VIDEO') {
      ctx.addIssue({ code: 'custom', message: 'Los videos no están disponibles en radio', path: ['showOnRadio'] });
    }
    if (data.showOnRadio && data.includeRadioStationIds.some((id) => data.excludeRadioStationIds.includes(id))) {
      ctx.addIssue({ code: 'custom', message: 'Una emisora no puede estar incluida y excluida', path: ['excludeRadioStationIds'] });
    }
    const overlap = data.includeChannelIds.filter((id) => data.excludeChannelIds.includes(id));
    if (overlap.length > 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'Un canal no puede estar en incluir y excluir',
        path: ['excludeChannelIds'],
      });
    }

    if (data.type === 'TEXT') {
      if (!data.text?.trim()) {
        ctx.addIssue({
          code: 'custom',
          message: 'El texto es requerido para anuncios de texto',
          path: ['text'],
        });
      }
      if (typeof data.textFontSize !== 'number' || Number.isNaN(data.textFontSize)) {
        ctx.addIssue({
          code: 'custom',
          message: 'El tamaño de fuente es requerido',
          path: ['textFontSize'],
        });
      }
      if (!data.textFontColor) {
        ctx.addIssue({
          code: 'custom',
          message: 'El color de fuente es requerido',
          path: ['textFontColor'],
        });
      }
      if (!data.textFontFamily) {
        ctx.addIssue({
          code: 'custom',
          message: 'La fuente es requerida',
          path: ['textFontFamily'],
        });
      }
      if (!data.textBackgroundColor) {
        ctx.addIssue({
          code: 'custom',
          message: 'El color de fondo es requerido',
          path: ['textBackgroundColor'],
        });
      }
      if (typeof data.textTickerSpeedSeconds !== 'number' || Number.isNaN(data.textTickerSpeedSeconds)) {
        ctx.addIssue({
          code: 'custom',
          message: 'La velocidad del ticker es requerida',
          path: ['textTickerSpeedSeconds'],
        });
      }
    }

    if (data.type === 'NEWS_TICKER') {
      const sourceType = data.newsTickerSourceType ?? 'MANUAL';
      const manualItems = (data.newsTickerItemsText ?? '')
        .split('\n')
        .map((item) => item.trim())
        .filter(Boolean);
      if ((sourceType === 'MANUAL' || sourceType === 'MANUAL_AND_RSS') && manualItems.length === 0) {
        ctx.addIssue({
          code: 'custom',
          message: 'Agrega al menos una noticia manual',
          path: ['newsTickerItemsText'],
        });
      }
      if (sourceType === 'RSS' || sourceType === 'MANUAL_AND_RSS') {
        const url = data.newsTickerRssUrl?.trim();
        if (!url) {
          ctx.addIssue({ code: 'custom', message: 'La URL RSS es requerida', path: ['newsTickerRssUrl'] });
        } else {
          try {
            new URL(url);
          } catch {
            ctx.addIssue({ code: 'custom', message: 'La URL RSS debe ser válida', path: ['newsTickerRssUrl'] });
          }
        }
      }
    }

    if (data.videoDismissible) {
      if (typeof data.videoMinSecondsBeforeDismiss !== 'number' || Number.isNaN(data.videoMinSecondsBeforeDismiss)) {
        ctx.addIssue({
          code: 'custom',
          message: 'El mínimo para cerrar es requerido',
          path: ['videoMinSecondsBeforeDismiss'],
        });
      }
    }

    if (data.type === 'VIDEO' && !data.showOnVod && !data.showOnLiveTv && !data.home.enabled) {
      ctx.addIssue({
        code: 'custom',
        message: 'Los anuncios de video deben mostrarse en al menos una superficie',
        path: ['showOnVod'],
      });
    }

    if (data.type === 'VIDEO' && data.showOnLiveTv && (!data.schedules || data.schedules.length === 0)) {
      ctx.addIssue({
        code: 'custom',
        message: 'Los anuncios de video en TV en vivo requieren al menos un horario recurrente',
        path: ['schedules'],
      });
    }

  });

type CampaignFormData = z.input<typeof campaignSchema>;

const creativeFields = ['type', 'videoMode', 'videoPlacements', 'adTagUrl', 'videoUrl', 'videoDismissible', 'videoMinSecondsBeforeDismiss', 'bannerImageUrl', 'bannerPosition', 'bannerScaleFactor', 'bannerOverlayEnabled', 'showTimeInSeconds', 'text', 'textItems', 'textFontSize', 'textFontColor', 'textFontFamily', 'textBackgroundColor', 'textTickerSpeedSeconds', 'newsTickerSourceType', 'newsTickerRssUrl', 'newsTickerMaxItems', 'newsTickerBackgroundImageUrl', 'newsTickerLeftImageUrl', 'newsTickerSeparator', 'newsTickerLoopEnabled', 'newsTickerItemsText', 'showOnLiveTv', 'showOnVod', 'showOnRadio', 'radioDisplayMode', 'home'] as const;
type CreativeDraft = { id?: string; name: string; enabled: boolean; values: CampaignFormData };
const creativeValues = (values: CampaignFormData) => Object.fromEntries(creativeFields.map(key => [key, values[key]]));
const creativeContent = (payload: CreateAdCampaignRequest): AdCreative['content'] => Object.fromEntries(
  [...creativeFields.filter(key => key !== 'newsTickerItemsText' && key !== 'bannerOverlayEnabled' && key !== 'videoMode' && key !== 'adTagUrl'), 'newsTickerItems'].map(key => [key, payload[key]])
);

const buildPayload = (values: CampaignFormData, statusOverride: AdCampaignStatus): CreateAdCampaignRequest => {

  return {
    name: values.name,
    home: { ...values.home, mediaUrl: (values.type === 'VIDEO' ? values.videoUrl : values.bannerImageUrl) ?? '', placement: values.type === 'VIDEO' || values.type === 'BANNER' ? 'HOME_SLIDE' : 'HOME_TICKER' },
    status: statusOverride,
    durationMode: values.durationMode,
    startAt: values.durationMode === 'DATE_RANGE' ? toIsoFromLocalDateTimeInput(values.startAt ?? '') : undefined,
    endAt: values.durationMode === 'DATE_RANGE' ? toIsoFromLocalDateTimeInput(values.endAt ?? '') : undefined,
    priority: values.priority,
    weight: values.weight,
    videoMode: values.videoMode,
    videoPlacements: values.showOnVod && values.type === 'VIDEO' ? ['PRE_ROLL'] : undefined,
    adTagUrl: values.videoMode === 'NONE' ? undefined : values.adTagUrl?.trim(),
    videoUrl: values.videoUrl,
    showOnLiveTv: values.showOnLiveTv || false,
    showOnVod: values.showOnVod,
    showOnRadio: values.type !== 'VIDEO' && values.showOnRadio,
    radioDisplayMode: 'BOTH',
    includeRadioStationIds: values.includeRadioStationIds,
    excludeRadioStationIds: values.excludeRadioStationIds,
    videoDismissible: values.videoDismissible,
    videoMinSecondsBeforeDismiss: values.videoMinSecondsBeforeDismiss,
    text: values.type === 'TEXT' ? values.text : undefined,
    textItems: values.type === 'TEXT' ? values.textItems : undefined,
    textFontSize: values.type === 'TEXT' || values.type === 'NEWS_TICKER' ? values.textFontSize : undefined,
    textFontColor: values.type === 'TEXT' || values.type === 'NEWS_TICKER' ? values.textFontColor : undefined,
    textFontFamily: values.type === 'TEXT' || values.type === 'NEWS_TICKER' ? values.textFontFamily : undefined,
    textBackgroundColor: values.type === 'TEXT' || values.type === 'NEWS_TICKER' ? values.textBackgroundColor : undefined,
    textTickerSpeedSeconds: values.type === 'TEXT' || values.type === 'NEWS_TICKER' ? values.textTickerSpeedSeconds : undefined,
    newsTickerSourceType: values.type === 'NEWS_TICKER' ? values.newsTickerSourceType : undefined,
    newsTickerRssUrl: values.type === 'NEWS_TICKER' ? values.newsTickerRssUrl?.trim() : undefined,
    newsTickerMaxItems: values.type === 'NEWS_TICKER' ? values.newsTickerMaxItems : undefined,
    newsTickerBackgroundImageUrl: values.type === 'NEWS_TICKER' ? values.newsTickerBackgroundImageUrl?.trim() : undefined,
    newsTickerLeftImageUrl: values.type === 'NEWS_TICKER' ? values.newsTickerLeftImageUrl?.trim() : undefined,
    newsTickerSeparator: values.type === 'NEWS_TICKER' ? values.newsTickerSeparator : undefined,
    newsTickerLoopEnabled: values.type === 'NEWS_TICKER' ? values.newsTickerLoopEnabled : undefined,
    newsTickerItems: values.type === 'NEWS_TICKER'
      ? (values.newsTickerItemsText ?? '').split('\n').map((item) => item.trim()).filter(Boolean)
      : undefined,
    bannerImageUrl: values.bannerImageUrl,
    type: values.type,
    showTimeInSeconds: values.showTimeInSeconds,
    bannerPosition:  values.bannerPosition,
    bannerScaleFactor: values.bannerScaleFactor,
    audience: values.audience,
    locationTargets: values.locationTargets,
    schedules: values.schedules?.map((schedule) => ({
      daysOfWeek: schedule.daysOfWeek,
      triggerTime: toApiLocalTime(schedule.triggerTime),
    })),
    includeChannelIds: values.includeChannelIds,
    excludeChannelIds: values.excludeChannelIds,
    maxImpressionsPerUserPerDay: values.maxImpressionsPerUserPerDay,
  };
};

const toDefaultValues = (campaign?: AdCampaign): CampaignFormData => {
  const bannerOverlayEnabled = !!campaign?.bannerOverlay;
  const showOnLiveTv = campaign?.showOnLiveTv ?? campaign?.showOnLiveTv ?? false;
  const showOnVod = campaign?.showOnVod ?? campaign?.showOnVod ?? false;

  return {
    name: campaign?.name || '',
    durationMode: campaign?.durationMode ?? ((campaign?.startAt || campaign?.endAt) ? 'DATE_RANGE' : 'UNTIL_DEACTIVATED'),
    type: campaign?.type || 'VIDEO',
    startAt: campaign?.startAt ? toLocalDateTimeInput(campaign.startAt) : '',
    endAt: campaign?.endAt ? toLocalDateTimeInput(campaign.endAt) : '',
    priority: campaign?.priority ?? 0,
    weight: campaign?.weight ?? 1,
    version: campaign?.version ?? 0,
    showOnLiveTv,
    showOnVod,
    home: { ...defaultHomeSettings, ...campaign?.home, placement: campaign?.type === 'TEXT' || campaign?.type === 'NEWS_TICKER' ? 'HOME_TICKER' : 'HOME_SLIDE', mediaUrl: campaign?.home?.mediaUrl ?? '' },
    showOnRadio: campaign?.type !== 'VIDEO' && (campaign?.showOnRadio ?? false),
    radioDisplayMode: 'BOTH',
    includeRadioStationIds: campaign?.includeRadioStationIds ?? [],
    excludeRadioStationIds: campaign?.excludeRadioStationIds ?? [],
    includeChannelIds: campaign?.includeChannelIds ?? campaign?.includedChannels?.map((channel) => channel.id) ?? [],
    excludeChannelIds: campaign?.excludeChannelIds ?? campaign?.excludedChannels?.map((channel) => channel.id) ?? [],
    audience: campaign?.audience ?? {},
    locationTargets: campaign?.locationTargets ?? [],
    schedules: campaign?.schedules?.map((schedule) => ({
      daysOfWeek: schedule.daysOfWeek ?? [],
      triggerTime: normalizeTimeInput(schedule.triggerTime),
    })) ?? [],
    maxImpressionsPerUserPerDay: campaign?.maxImpressionsPerUserPerDay || null,
    videoMode: campaign?.videoMode ?? 'NONE',
    videoPlacements: ['PRE_ROLL'],
    adTagUrl: campaign?.adTagUrl ?? undefined,
    videoUrl: campaign?.videoUrl ?? undefined,
    videoDismissible: campaign?.videoDismissible ?? true,
    videoMinSecondsBeforeDismiss: campaign?.videoMinSecondsBeforeDismiss ?? 5,
    text: campaign?.text ?? undefined,
    textItems: campaign?.textItems ?? [],
    textFontSize: campaign?.textFontSize ?? 24,
    textFontColor: campaign?.textFontColor ?? '#ffffff',
    textFontFamily: campaign?.textFontFamily ?? TEXT_AD_DEFAULT_FONT,
    textBackgroundColor: campaign?.textBackgroundColor ?? '#111827',
    textTickerSpeedSeconds: campaign?.textTickerSpeedSeconds ?? 12,
    newsTickerSourceType: campaign?.newsTickerSourceType ?? 'MANUAL',
    newsTickerRssUrl: campaign?.newsTickerRssUrl ?? '',
    newsTickerMaxItems: campaign?.newsTickerMaxItems ?? 10,
    newsTickerBackgroundImageUrl: campaign?.newsTickerBackgroundImageUrl ?? '',
    newsTickerLeftImageUrl: campaign?.newsTickerLeftImageUrl ?? '',
    newsTickerSeparator: campaign?.newsTickerSeparator ?? ' • ',
    newsTickerLoopEnabled: campaign?.newsTickerLoopEnabled ?? true,
    newsTickerItemsText: campaign?.newsTickerItems?.join('\n') ?? '',
    bannerOverlayEnabled,
    bannerPosition: campaign?.bannerPosition || 'BOTTOM',
    bannerImageUrl: campaign?.bannerImageUrl || undefined,
    bannerScaleFactor: campaign?.bannerScaleFactor ?? 1,
    showTimeInSeconds: campaign?.showTimeInSeconds || 5,
  };
};

type CampaignPreviewPanelProps = {
  adType: CampaignFormData['type'] | null;
  durationMode: AdCampaignDurationMode;
  startAt?: string;
  endAt?: string;
  scheduleCount: number;
  bannerImageUrl?: string;
  bannerPosition: BannerOverlayPosition;
  bannerScaleFactor: number;
  textValue: string;
  textItems?: RichTextItem[];
  textFontSize: number;
  textFontColor: string;
  textFontFamily: string;
  textBackgroundColor: string;
  textTickerSpeedSeconds: number;
  textPosition: BannerOverlayPosition;
  videoUrl?: string;
  showTimeInSeconds: number;
};

const CampaignPreviewPanel = ({
  adType,
  durationMode,
  startAt,
  endAt,
  scheduleCount,
  bannerImageUrl,
  bannerPosition,
  bannerScaleFactor,
  textValue,
  textItems,
  textFontSize,
  textFontColor,
  textFontFamily,
  textBackgroundColor,
  textTickerSpeedSeconds,
  textPosition,
  videoUrl,
  showTimeInSeconds,
}: CampaignPreviewPanelProps) => {
  const normalizeColor = (value: string, fallback: string) => {
    const trimmed = value?.trim();
    if (!trimmed) return fallback;
    if (trimmed === 'transparent') return 'transparent';
    return trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
  };

  const resolvedFontColor = normalizeColor(textFontColor, '#ffffff');
  const resolvedBackgroundColor = normalizeColor(textBackgroundColor, '#111827');
  const resolvedFontFamily = resolveTextAdFontOption(textFontFamily).cssFamily;
  const previewText = textValue || 'Escribe el mensaje del anuncio para ver el ticker.';
  const tickerHeight = Math.max(48, Math.round(Math.max(textFontSize, ...(textItems ?? []).map(item => item.style?.fontSize ?? textFontSize)) * 1.7));
  const bannerHeight = Math.max(50, Math.round(80 * Math.min(Math.max(bannerScaleFactor, 0.3), 3)));
  const scheduleSummary =
    durationMode === 'UNTIL_DEACTIVATED'
      ? 'Hasta desactivar'
      : startAt && endAt
        ? `${startAt.replace('T', ' ')} → ${endAt.replace('T', ' ')}`
        : 'Completa el rango de fechas';

  return (
    <aside className="self-start">
      <div className="border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-100 px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Vista previa</h2>
            </div>
          </div>
        </div>

        <div className="space-y-4 p-4">
          <div className="overflow-hidden border border-gray-200 bg-gray-950 shadow-inner">
            <div className="relative aspect-video bg-[radial-gradient(circle_at_top_left,_rgba(255,255,255,0.14),_transparent_32%),linear-gradient(135deg,_#0f172a,_#020617)]">
              <div className="absolute inset-0 bg-[linear-gradient(transparent_0%,rgba(255,255,255,0.03)_100%)]" />

              {adType === 'VIDEO' && (
                <div className="absolute inset-0">
                  {videoUrl ? (
                    <video
                      key={videoUrl}
                      src={videoUrl}
                      className="h-full w-full object-cover"
                      autoPlay
                      loop
                      muted
                      playsInline
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center px-6 text-center text-sm text-white/70">
                      Sube un video para ver la reproduccion simulada.
                    </div>
                  )}
                  <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-gradient-to-t from-black/70 to-transparent px-4 py-3 text-xs text-white/80">
                    <span>Playback preview</span>
                    <span>{showTimeInSeconds}s</span>
                  </div>
                </div>
              )}

              {adType === 'BANNER' && (
                <>
                  <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22120%22 height=%22120%22 viewBox=%220 0 120 120%22%3E%3Cg fill=%22none%22 fill-rule=%22evenodd%22%3E%3Cg fill=%22%23ffffff%22 fill-opacity=%220.05%22%3E%3Cpath d=%22M0 0h60v60H0zM60 60h60v60H60z%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-30" />
                  <div
                    className="absolute left-4 right-4 overflow-hidden border border-white/15 bg-black/25 shadow-lg backdrop-blur-sm"
                    style={{
                      height: `${bannerHeight}px`,
                      top: bannerPosition === 'TOP' ? '16px' : undefined,
                      bottom: bannerPosition === 'BOTTOM' ? '16px' : undefined,
                    }}
                  >
                    {bannerImageUrl ? (
                      <img
                        src={bannerImageUrl}
                        alt="Vista previa del banner"
                        className="h-full w-full object-contain"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center px-6 text-center text-sm text-white/70">
                        Sube una imagen para ver la imagen en pantalla.
                      </div>
                    )}
                  </div>
                </>
              )}

              {adType === 'TEXT' && (
                <>
                  <style>
                    {`
                      @keyframes campaign-preview-ticker {
                        0% { transform: translateX(100%); }
                        100% { transform: translateX(-100%); }
                      }
                    `}
                  </style>
                  <div
                    className="absolute left-0 right-0 overflow-hidden"
                    style={{
                      height: `${tickerHeight}px`,
                      top: textPosition === 'TOP' ? '0' : undefined,
                      bottom: textPosition === 'BOTTOM' ? '0' : undefined,
                      backgroundColor: resolvedBackgroundColor,
                    }}
                  >
                    <div className="flex h-full items-center px-4">
                      <div
                        className="whitespace-nowrap"
                        style={{
                          color: resolvedFontColor,
                          fontFamily: resolvedFontFamily,
                          fontSize: `${Math.max(14, Math.min(textFontSize, 42))}px`,
                          animation: `campaign-preview-ticker ${textTickerSpeedSeconds}s linear infinite`,
                        }}
                      >
                        {textItems?.length ? <RichMessagePreview items={textItems} /> : previewText}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="bg-gray-50 px-3 py-2">
              <div className="text-gray-500">Formato</div>
              <div className="mt-1 font-medium text-gray-900">{adType ?? 'VIDEO'}</div>
            </div>
            <div className="bg-gray-50 px-3 py-2">
              <div className="text-gray-500">Duracion</div>
              <div className="mt-1 font-medium text-gray-900">{showTimeInSeconds}s</div>
            </div>
          </div>

          <div className="border border-gray-200 bg-gray-50 px-3 py-3 text-xs">
            <div className="text-gray-500">Programación</div>
            <div className="mt-1 font-medium text-gray-900">{scheduleSummary}</div>
            <div className="mt-1 text-gray-600">
              {scheduleCount > 0 ? `${scheduleCount} horario(s) recurrente(s)` : 'Sin horarios recurrentes'}
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
};

const CampaignForm = ({ mode, id }: { mode: WizardMode; id?: string }) => {
  const { branding } = useBranding();
  const audienceEnabled = Boolean(branding.modules?.audienceManagementEnabled);
  const navigate = useNavigate();
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const imageFileInputRef = useRef<HTMLInputElement | null>(null);
  const [isDraggingImage, setIsDraggingImage] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [videoUploadProgress, setVideoUploadProgress] = useState(0);
  const videoFileInputRef = useRef<HTMLInputElement | null>(null);
  const [isDraggingVideo, setIsDraggingVideo] = useState(false);
  const lastNonTransparentBgRef = useRef<string>('#111827');
  const [isFontDropdownOpen, setIsFontDropdownOpen] = useState(false);
  const fontDropdownRef = useRef<HTMLDivElement | null>(null);

  const [creatives, setCreatives] = useState<CreativeDraft[]>([]);
  const [campaignLocations, setCampaignLocations] = useState({ live: false, vod: false, home: false, radio: false });
  const [renamingCreative, setRenamingCreative] = useState<number | null>(null);
  const [selectedCreative, setSelectedCreative] = useState(0);
  const [editorSection, setEditorSection] = useState<'campaign' | 'creatives' | 'segmentation'>('campaign');
  const isEdit = mode === 'edit';
  const campaignQuery = useAdCampaign(isEdit ? (id || '') : '');
  const createMutation = useCreateAdCampaign();
  const updateMutation = useUpdateAdCampaign();

  const { data: channels } = useTVChannelsForDropdown();


  const channelOptions: MultiSelectOption[] = useMemo(() => {
    return (channels || []).map((ch: TVChannel) => ({ value: ch.id, label: ch.title }));
  }, [channels]);

  const form = useForm<CampaignFormData>({
    resolver: zodResolver(campaignSchema),
    defaultValues: toDefaultValues(undefined),
    mode: 'onBlur',
  });

  const {
    register,
    setValue,
    setError,
    clearErrors,
    reset,
    watch,
    formState: { errors, isSubmitting },
    trigger,
  } = form;

  const showOnLiveTv = watch('showOnLiveTv');
  const showOnVod = watch('showOnVod') ?? false;
  const { t: homeTranslations } = useTranslation();
  const homeLabels = homeTranslations.homeCampaign;
  const creativeLabels = homeTranslations.adCreatives;
  const editorSections: Array<'campaign' | 'creatives' | 'segmentation'> = audienceEnabled ? ['campaign', 'creatives', 'segmentation'] : ['campaign', 'creatives'];
  const adType = watch('type') ?? null;
  const durationMode = watch('durationMode') ?? 'DATE_RANGE';
  const startAtValue = watch('startAt') ?? '';
  const endAtValue = watch('endAt') ?? '';
  const includeChannelIds = watch('includeChannelIds');
  const excludeChannelIds = watch('excludeChannelIds');
  const bannerImageUrl = watch('bannerImageUrl');
  const videoUrl = watch('videoUrl');
  const videoDismissible = watch('videoDismissible') ?? true;
  const maxImpressionsPerUserPerDay = watch('maxImpressionsPerUserPerDay');

  const [channelMode, setChannelMode] = useState<ChannelMode>('ALL');
  const lastMaxImpressionsRef = useRef<number>(0);

  useEffect(() => {
    if (typeof maxImpressionsPerUserPerDay === 'number' && !Number.isNaN(maxImpressionsPerUserPerDay)) {
      lastMaxImpressionsRef.current = maxImpressionsPerUserPerDay;
    }
  }, [maxImpressionsPerUserPerDay]);

  useEffect(() => {
    if (includeChannelIds.length > 0) {
      setChannelMode('INCLUDE');
      return;
    }
    if (excludeChannelIds.length > 0) {
      setChannelMode('EXCLUDE');
    }
  }, [includeChannelIds, excludeChannelIds]);
  const bannerPosition = watch('bannerPosition') || 'BOTTOM';
  const bannerScaleFactor = watch('bannerScaleFactor') ?? 1;
  const textValue = watch('text') || '';
  const textFontSize = watch('textFontSize') || 24;
  const textFontColor = watch('textFontColor') || '#ffffff';
  const textFontFamily = watch('textFontFamily') || TEXT_AD_DEFAULT_FONT;
  const textBackgroundColor = watch('textBackgroundColor') || '#111827';
  const textTickerSpeedSeconds = watch('textTickerSpeedSeconds') || 12;
  const textPosition = watch('bannerPosition') || 'BOTTOM';
  const newsTickerSourceType = watch('newsTickerSourceType') || 'MANUAL';
  const showTimeInSeconds = watch('showTimeInSeconds') || 5;
  const selectedTextFont = resolveTextAdFontOption(textFontFamily);
  const isTransparentBackground = textBackgroundColor === 'transparent';
  const locationTargets = watch('locationTargets') ?? [];
  const schedules = watch('schedules') ?? [];
  const priorityValue = watch('priority');
  const weightValue = watch('weight');
  const priorityRegister = register('priority', { valueAsNumber: true });
  const weightRegister = register('weight', { valueAsNumber: true });

  const priorityHelperText =
    typeof priorityValue === 'number' && !Number.isNaN(priorityValue) && priorityValue > 100
      ? 'Las campañas con mayor prioridad se consideran primero. El valor actual supera 100.'
      : 'Las campañas con mayor prioridad se consideran primero.';

  const weightHelperText = (() => {
    if (typeof weightValue === 'number' && !Number.isNaN(weightValue)) {
      if (weightValue <= 0) {
        return 'Balancea la entrega entre campañas con la misma prioridad. Mayor peso = mayor probabilidad de selección. El mínimo es 1 y se ajustará automáticamente.';
      }
      if (weightValue > 100) {
        return 'Balancea la entrega entre campañas con la misma prioridad. Mayor peso = mayor probabilidad de selección. El valor actual supera 100.';
      }
    }
    return 'Balancea la entrega entre campañas con la misma prioridad. Mayor peso = mayor probabilidad de selección.';
  })();

  const updateChannelMode = (mode: ChannelMode) => {
    setChannelMode(mode);
    if (mode === 'ALL') {
      setValue('includeChannelIds', [], { shouldDirty: true, shouldValidate: true });
      setValue('excludeChannelIds', [], { shouldDirty: true, shouldValidate: true });
      return;
    }
    if (mode === 'INCLUDE') {
      setValue('excludeChannelIds', [], { shouldDirty: true, shouldValidate: true });
      return;
    }
    setValue('includeChannelIds', [], { shouldDirty: true, shouldValidate: true });
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!fontDropdownRef.current) return;
      if (!fontDropdownRef.current.contains(event.target as Node)) {
        setIsFontDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (textBackgroundColor && textBackgroundColor !== 'transparent') {
      lastNonTransparentBgRef.current = textBackgroundColor;
    }
  }, [textBackgroundColor]);

  useEffect(() => {
    if (!isEdit) return;
    if (!campaignQuery.data) return;
    const campaign = campaignQuery.data;
    const loaded: CreativeDraft[] = campaign.creatives?.length
      ? campaign.creatives.map(creative => ({ ...creative, values: toDefaultValues({ ...campaign, ...creative.content }) }))
      : [{ name: campaign.name, enabled: true, values: toDefaultValues(campaign) }];
    setCampaignLocations({
      live: loaded.some(item => item.values.showOnLiveTv),
      vod: loaded.some(item => item.values.showOnVod),
      home: loaded.some(item => item.values.home.enabled),
      radio: loaded.some(item => item.values.showOnRadio),
    });
    setCreatives(loaded);
    setSelectedCreative(0);
    reset(loaded[0].values);
  }, [campaignQuery.data, isEdit, reset]);

  const addSchedule = () => {
    setValue('schedules', [...schedules, emptySchedule()], { shouldDirty: true, shouldValidate: true });
  };

  const removeSchedule = (index: number) => {
    setValue(
      'schedules',
      schedules.filter((_, idx) => idx !== index),
      { shouldDirty: true, shouldValidate: true }
    );
  };

  const updateSchedule = (index: number, nextSchedule: AdCampaignSchedule) => {
    setValue(
      'schedules',
      schedules.map((schedule, idx) => (idx === index ? nextSchedule : schedule)),
      { shouldDirty: true, shouldValidate: true }
    );
  };

  const toggleScheduleDay = (index: number, day: AdScheduleDayOfWeek) => {
    const schedule = schedules[index] ?? emptySchedule();
    const exists = schedule.daysOfWeek.includes(day);
    const nextDays = exists
      ? schedule.daysOfWeek.filter((value) => value !== day)
      : [...schedule.daysOfWeek, day];
    updateSchedule(index, { ...schedule, daysOfWeek: nextDays });
  };

  const snapshotCreatives = () => creatives.map((creative, index) => index === selectedCreative
    ? { ...creative, values: form.getValues() } : creative);

  const selectCreative = (index: number) => {
    const current = snapshotCreatives();
    setCreatives(current);
    reset({ ...form.getValues(), ...creativeValues(current[index].values) });
    setSelectedCreative(index);
  };

  const addCreative = (type: AdCampaignType) => {
    const current = snapshotCreatives();
    const values = { ...toDefaultValues(undefined), type };
    const next: CreativeDraft = { name: creativeLabels.formats[type], enabled: true, values };
    setCreatives([...current, next]);
    reset({ ...form.getValues(), ...creativeValues(values) });
    setSelectedCreative(current.length);
    setRenamingCreative(null);
  };

  const removeCreative = (index: number) => {
    const current = snapshotCreatives().filter((_, position) => position !== index);
    const nextIndex = Math.max(0, Math.min(selectedCreative - (index < selectedCreative ? 1 : 0), current.length - 1));
    setCreatives(current);
    reset({ ...form.getValues(), ...creativeValues(current[nextIndex]?.values ?? toDefaultValues(undefined)) });
    setSelectedCreative(nextIndex);
    setRenamingCreative(null);
  };

  const changeCampaignLocation = (location: keyof typeof campaignLocations, enabled: boolean) => {
    setCampaignLocations(current => ({ ...current, [location]: enabled }));
    if (enabled) return;
    const clearLocation = (values: CampaignFormData): CampaignFormData => location === 'home'
      ? { ...values, home: { ...values.home, enabled: false } }
      : { ...values, [location === 'live' ? 'showOnLiveTv' : location === 'vod' ? 'showOnVod' : 'showOnRadio']: false };
    setCreatives(snapshotCreatives().map(creative => ({ ...creative, values: clearLocation(creative.values) })));
    reset(clearLocation(form.getValues()));
  };

  const save = async (statusOverride: AdCampaignStatus) => {
    if (createMutation.isPending || updateMutation.isPending || uploadingImage || uploadingVideo) return;
    if (!creatives.length) {
      setEditorSection('creatives');
      toast.error(creativeLabels.empty);
      return;
    }
    const values = form.getValues();
    const drafts = snapshotCreatives();
    const missingLocations = (Object.keys(campaignLocations) as Array<keyof typeof campaignLocations>).filter(location =>
      campaignLocations[location] && !drafts.some(({ values: draft }) => location === 'home' ? draft.home.enabled
        : location === 'live' ? draft.showOnLiveTv : location === 'vod' ? draft.showOnVod : draft.showOnRadio && draft.type !== 'VIDEO'));
    if (missingLocations.length) {
      setEditorSection('creatives');
      toast.error(`${creativeLabels.missingLocations}: ${missingLocations.map(location => creativeLabels[location]).join(', ')}`);
      return;
    }
    const output: AdCreative[] = [];
    for (let index = 0; index < drafts.length; index++) {
      const creative = drafts[index];
      const merged = { ...values, ...creativeValues(creative.values) };
      const items = creative.values.textItems ?? [];
      const invalidRichMessage = merged.type === 'TEXT' && items.length > 0 && (
        items.length > 24 || items.filter(item => item.kind === 'IMAGE').length > 8 ||
        messageFallback(items).length > 500 || items.some(item =>
          item.kind === 'IMAGE' ? !/^https?:\/\/[^\s/]+/i.test(item.imageUrl ?? '') || !item.alt?.trim() || item.alt.length > 80 :
          item.kind === 'STICKER' ? !stickers[item.sticker ?? ''] :
          !item.text?.trim() || item.text.length > (item.kind === 'EMOJI' ? 32 : 500))
      );
      if (invalidRichMessage) {
        setCreatives(drafts); setSelectedCreative(index); setEditorSection('creatives'); reset(merged);
        setError('text', { type: 'manual', message: homeTranslations.richMessage.invalid });
        toast.error(homeTranslations.richMessage.invalid);
        return;
      }
      const valid = campaignSchema.safeParse(merged);
      if (!valid.success || !creative.name.trim() || creative.name.length > 200) {
        setCreatives(drafts);
        setSelectedCreative(index);
        const invalidField = !valid.success ? valid.error.issues[0]?.path[0] : undefined;
        setEditorSection(audienceEnabled && (invalidField === 'audience' || invalidField === 'locationTargets') ? 'segmentation' : invalidField && !creativeFields.includes(invalidField as typeof creativeFields[number]) ? 'campaign' : 'creatives');
        reset(merged);
        await trigger();
        toast.error(`${creativeLabels.review}: ${creative.name || index + 1}`);
        return;
      }
      output.push({ id: creative.id, name: creative.name.trim(), enabled: true, content: creativeContent(buildPayload(merged, statusOverride)) });
    }
    const payload = { ...buildPayload(values, statusOverride), creatives: output,
      ...(!audienceEnabled ? { audience: undefined, locationTargets: undefined } : {}) };

    try {
      if (isEdit) {
        const updated = await updateMutation.mutateAsync({
          id: id || '',
          data: { ...payload, version: values.version }
        });
        reset(toDefaultValues(updated));
        toast.success('Campaña guardada');
        navigate(`/ads/campaigns/${updated.id}`);
        return;
      }

      const created = await createMutation.mutateAsync(payload);
      console.log("Created campaign", created);
      toast.success('Campaña creada');
      navigate(`/ads/campaigns/${created.id}`);
    } catch (error) {
      if (error instanceof ApiClientError && (error.status === 409 || error.status === 412)) {
        toast.error('La campaña cambió en el servidor. Recarga e intenta de nuevo.');
      } else {
        toast.error(error instanceof Error ? error.message : 'No se pudo guardar');
      }
    }
  };


  const uploadBannerImage = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setError('bannerImageUrl', {
        type: 'manual',
        message: 'El archivo debe ser una imagen válida.',
      });
      return;
    }

    setUploadingImage(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('source', form.getValues('home').enabled ? MediaSource.HOME_SLIDE : MediaSource.AD_IMAGE);

    try {
      clearErrors('bannerImageUrl');
      const response = await apiClient.post<{ success: boolean; url: string }>(
        '/media/upload',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (event) => {
            if (!event.total) return;
            const progress = Math.round((event.loaded * 100) / event.total);
            setUploadProgress(progress);
          },
        }
      );

      if (response.success && response.url) {
        setValue('bannerImageUrl', response.url, {
          shouldValidate: true,
          shouldDirty: true,
        });
        clearErrors('bannerImageUrl');
        toast.success('Imagen subida');
      } else {
        setError('bannerImageUrl', {
          type: 'manual',
          message: 'No se pudo subir la imagen.',
        });
      }
    } catch (error) {
      setError('bannerImageUrl', {
        type: 'manual',
        message: error instanceof Error ? error.message : 'No se pudo subir la imagen.',
      });
    } finally {
      setUploadingImage(false);
    }
  };

  const handleBannerFile = (file?: File) => {
    if (!file) return;
    clearErrors('bannerImageUrl');
    void uploadBannerImage(file);
  };

  const uploadVideoFile = async (file: File) => {
    if (!file.type.startsWith('video/')) {
      setError('videoUrl', {
        type: 'manual',
        message: 'El archivo debe ser un video válido.',
      });
      return;
    }

    setUploadingVideo(true);
    setVideoUploadProgress(0);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('source', MediaSource.AD_VIDEO);

    try {
      clearErrors('videoUrl');
      const response = await apiClient.post<{ success: boolean; url: string }>(
        '/media/upload',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (event) => {
            if (!event.total) return;
            const progress = Math.round((event.loaded * 100) / event.total);
            setVideoUploadProgress(progress);
          },
        }
      );

      if (response.success && response.url) {
        setValue('videoUrl', response.url, {
          shouldValidate: true,
          shouldDirty: true,
        });
        clearErrors('videoUrl');
        toast.success('Video subido');
      } else {
        setError('videoUrl', {
          type: 'manual',
          message: 'No se pudo subir el video.',
        });
      }
    } catch (error) {
      setError('videoUrl', {
        type: 'manual',
        message: error instanceof Error ? error.message : 'No se pudo subir el video.',
      });
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleVideoFile = (file?: File) => {
    if (!file) return;
    clearErrors('videoUrl');
    void uploadVideoFile(file);
  };

  if (isEdit && campaignQuery.isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white shadow p-8 flex items-center justify-center text-gray-600">
            <Loader2 className="w-5 h-5 animate-spin mr-2"/> Cargando...
          </div>
        </div>
      </div>
    );
  }

  if (isEdit && campaignQuery.isError) {
    return (
      <div className="h-full min-h-0 bg-gray-50 py-4">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white shadow p-6">
            <div className="text-red-600 font-medium">No se pudo cargar la campaña</div>
            <div className="text-gray-600 mt-2">{(campaignQuery.error as Error)?.message}</div>
            <div className="mt-4">
              <Button onClick={() => navigate('/ads/campaigns')} variant="outline">
                Volver
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const fontSelector = (
    <div className="relative space-y-1" ref={fontDropdownRef}>
      <label className="block text-sm font-medium text-gray-700">Fuente</label>
      <button
        type="button"
        className={`
          flex w-full items-center justify-between rounded-md border bg-white px-3 py-1 text-left shadow-sm
          focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
          ${errors.textFontFamily?.message ? 'border-red-300' : 'border-gray-300'}
        `}
        onClick={() => setIsFontDropdownOpen((prev) => !prev)}
      >
        <span
          className="truncate text-sm text-gray-900"
          style={{ fontFamily: selectedTextFont.cssFamily }}
        >
          {selectedTextFont.label}
        </span>
        <span className="ml-3 text-gray-400">▼</span>
      </button>
      {isFontDropdownOpen && (
        <div className="absolute z-30 mt-1 max-h-60 w-full overflow-auto border border-gray-200 bg-white shadow-lg">
          {TEXT_AD_FONT_OPTIONS.map((font) => (
            <button
              key={font.value}
              type="button"
              className={`w-full px-3 py-2 text-left hover:bg-gray-100 ${
                textFontFamily === font.value ? 'bg-indigo-50' : ''
              }`}
              onClick={() => {
                setValue('textFontFamily', font.value, { shouldDirty: true, shouldValidate: true });
                setIsFontDropdownOpen(false);
              }}
            >
              <div className="text-sm text-gray-900" style={{ fontFamily: font.cssFamily }}>
                {font.preview}
              </div>
            </button>
          ))}
        </div>
      )}
      {errors.textFontFamily?.message && (
        <p className="text-xs text-red-600">{errors.textFontFamily?.message}</p>
      )}
    </div>
  );

  return (
    <div className="h-full min-h-0 bg-gray-50 py-4">
      <div className="h-full min-h-0 px-4 sm:px-6 lg:px-8">
        <div className="flex h-full min-h-0 flex-col gap-6 xl:grid xl:grid-cols-[minmax(0,1fr)_min(56vw,52rem)_minmax(0,1fr)] xl:grid-rows-[minmax(0,1fr)]">
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-white shadow-sm xl:col-start-2 xl:row-start-1">
            <div className="flex shrink-0 flex-wrap items-center justify-between gap-4 border-b border-gray-100 px-5 py-4 sm:px-6">
              <div>
                <h1 className="text-xl font-semibold text-gray-900">{isEdit ? 'Editar campaña' : 'Crear campaña'}</h1>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => navigate(-1)}>
                  Cancelar
                </Button>
                <Button type="button" variant="outline" onClick={() => save('DRAFT')} disabled={isSubmitting || createMutation.isPending || updateMutation.isPending || uploadingImage || uploadingVideo}>
                  {isSubmitting ? 'Guardando...' : 'Guardar borrador'}
                </Button>
                <Button type="button" onClick={() => save('ACTIVE')} disabled={isSubmitting || createMutation.isPending || updateMutation.isPending || uploadingImage || uploadingVideo}>
                  {isSubmitting ? 'Guardando...' :  'Guardar y activar'}
                </Button>
              </div>
            </div>

          <form
            className="flex min-h-0 flex-1 flex-col"
            onSubmit={async event => { event.preventDefault(); await save('DRAFT'); }}
          >
            <div className="flex shrink-0 flex-wrap gap-x-6 border-b border-gray-200" role="tablist" aria-label={creativeLabels.settings}>
              {editorSections.map(section => (
                <button
                  key={section}
                  id={`campaign-editor-tab-${section}`}
                  type="button"
                  role="tab"
                  aria-selected={editorSection === section}
                  aria-controls={section === 'campaign' ? 'campaign-name-panel campaign-settings-panel' : section === 'segmentation' ? 'campaign-segmentation-panel' : 'campaign-creatives-panel'}
                  tabIndex={editorSection === section ? 0 : -1}
                  onClick={() => setEditorSection(section)}
                  onKeyDown={event => {
                    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
                    event.preventDefault();
                    const index = editorSections.indexOf(section);
                    const next = event.key === 'Home' ? editorSections[0] : event.key === 'End' ? editorSections[editorSections.length - 1] : editorSections[(index + (event.key === 'ArrowRight' ? 1 : -1) + editorSections.length) % editorSections.length];
                    setEditorSection(next);
                    document.getElementById(`campaign-editor-tab-${next}`)?.focus();
                  }}
                  className={`-mb-px cursor-pointer border-b-2 py-3 first:pl-5 sm:first:pl-6 text-sm font-medium focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500 ${
                    editorSection === section
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                  }`}
                >
                  {section === 'campaign' ? creativeLabels.settings : section === 'segmentation' ? homeTranslations.audience.segmentation : `${creativeLabels.title} (${creatives.length})`}
                </button>
              ))}
            </div>
            <div className="min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-contain px-5 py-5 sm:px-6">
            <section id="campaign-name-panel" role="tabpanel" aria-labelledby="campaign-editor-tab-campaign" hidden={editorSection !== 'campaign'} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Input size="sm" label="Nombre" error={errors.name?.message} {...register('name')} />
                </div>
              </div>
            </section>
            <div id="campaign-creatives-panel" role="tabpanel" aria-labelledby="campaign-editor-tab-creatives" hidden={editorSection !== 'creatives'} className="rounded-lg border border-gray-300">
            <div className="flex flex-wrap items-end gap-4 rounded-t-lg border-b border-gray-300 bg-gray-50 px-4 sm:px-5">
              <div className="flex flex-wrap gap-4" role="tablist" aria-label={creativeLabels.title}>
                {creatives.map((creative, index) => (
                  <div key={creative.id ?? index} className={`group flex items-center gap-1 border-b-2 ${index === selectedCreative ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500'}`}>
                    {renamingCreative === index ? (
                      <input autoFocus name="creativeName" aria-label={creativeLabels.name} maxLength={200}
                        className="my-2 w-40 rounded border border-indigo-300 px-2 py-1 text-sm"
                        value={creative.name}
                        onChange={event => setCreatives(current => current.map((item, position) => position === index ? { ...item, name: event.target.value } : item))}
                        onBlur={() => {
                          setCreatives(current => current.map((item, position) => position === index ? { ...item, name: item.name.trim() || creativeLabels.formats[item.values.type ?? 'VIDEO'] } : item));
                          setRenamingCreative(null);
                        }}
                        onKeyDown={event => { if (event.key === 'Enter' || event.key === 'Escape') { event.preventDefault(); event.currentTarget.blur(); } }} />
                    ) : (
                      <button type="button" role="tab" aria-selected={index === selectedCreative}
                        disabled={uploadingImage || uploadingVideo} onClick={() => selectCreative(index)}
                        className="cursor-pointer px-1 py-3 text-sm font-medium">{creative.name}</button>
                    )}
                    <div className="flex opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100">
                      <button type="button" aria-label={`${creativeLabels.rename}: ${creative.name}`} title={creativeLabels.rename}
                        disabled={uploadingImage || uploadingVideo} onClick={() => setRenamingCreative(index)} className="rounded p-1 hover:bg-indigo-50"><Pencil size={14} /></button>
                      <button type="button" aria-label={`${creativeLabels.remove}: ${creative.name}`} title={creativeLabels.remove}
                        disabled={uploadingImage || uploadingVideo} onClick={() => removeCreative(index)} className="rounded p-1 hover:bg-red-50 hover:text-red-600"><Trash2 size={14} /></button>
                    </div>
                  </div>
                ))}
              </div>
              <div>
                <CampaignTypeCards value={null} onChange={addCreative} triggerLabel={creativeLabels.add}
                  disabled={creatives.length >= 50 || uploadingImage || uploadingVideo} />
              </div>
            </div>
            {!creatives.length && <p className="py-6 text-center text-sm text-gray-500">{creativeLabels.empty}</p>}
            {creatives.length > 0 && <div className="divide-y divide-gray-200">
            <section className="space-y-4 p-4 sm:p-5">
              {adType == 'VIDEO' && (
                <div className="space-y-4">
                  <input
                    ref={videoFileInputRef}
                    type="file"
                    accept="video/*"
                    className="hidden"
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (!file) return;
                      handleVideoFile(file);
                      event.target.value = '';
                    }}
                  />
                  <div
                    role="button"
                    tabIndex={0}
                    className={`w-full border-2 border-dashed p-4 transition ${
                      isDraggingVideo
                        ? 'border-[color:var(--brand-primary)] bg-gray-50'
                        : 'border-gray-300 bg-white'
                    }`}
                    onClick={() => videoFileInputRef.current?.click()}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        videoFileInputRef.current?.click();
                      }
                    }}
                    onDragOver={(event) => {
                      event.preventDefault();
                      setIsDraggingVideo(true);
                    }}
                    onDragLeave={() => setIsDraggingVideo(false)}
                    onDrop={(event) => {
                      event.preventDefault();
                      setIsDraggingVideo(false);
                      const file = event.dataTransfer.files?.[0];
                      handleVideoFile(file);
                    }}
                  >
                    <div className="flex flex-col gap-3">
                    <div>
                      <p className="text-sm font-medium text-gray-800">Archivo de video</p>
                      <p className="text-xs text-gray-500">Haz clic o arrastra el video aquí.</p>
                    </div>
                    <div className="border border-gray-200 bg-gray-50 p-3">
                      <p className="mb-1 text-xs font-semibold text-gray-700">Recomendaciones</p>
                      <ul className="space-y-1 text-xs text-gray-600">
                        <li>Resolución recomendada: 1280×720 px. Máximo sugerido: 1920×1080 px.</li>
                        <li>Codec de video: H.264 / AVC para máxima compatibilidad en Android.</li>
                        <li>Codec de audio: AAC-LC, estéreo, 44.1 kHz o 48 kHz.</li>
                        <li>Formato contenedor: MP4.</li>
                        <li>Extensión recomendada: `.mp4`.</li>
                        <li>Evita HEVC/H.265, VP9, AV1 y 4K si necesitas soporte en dispositivos Android antiguos.</li>
                      </ul>
                    </div>
                    {videoUrl ? (
                      <div className="flex flex-col gap-2">
                        <div className="text-xs text-gray-500 flex items-center">
                          <div>Archivo cargado</div>
                          <CheckCircle className="h-4 text-green-600"/>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            loading={uploadingVideo}
                            onClick={(event) => {
                              event.stopPropagation();
                              videoFileInputRef.current?.click();
                            }}
                          >
                            {uploadingVideo ? `Subiendo ${videoUploadProgress || 0}%` : 'Reemplazar video'}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                          <Button type="button" variant="outline" size="sm" loading={uploadingVideo}>
                            {uploadingVideo ? `Subiendo ${videoUploadProgress || 0}%` : 'Subir video'}
                          </Button>
                        </div>
                      )}

                      {uploadingVideo && (
                        <div className="w-full h-1 bg-gray-200 overflow-hidden">
                          <div
                            className="h-full bg-[color:var(--brand-primary)] transition-all duration-200"
                            style={{ width: `${videoUploadProgress}%` }}
                          />
                        </div>
                      )}
                      {errors.videoUrl?.message && (
                        <p className="text-xs text-red-600">{errors.videoUrl?.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-3 pt-6">
                      <Toggle
                        checked={videoDismissible}
                        onChange={(value) => setValue('videoDismissible', value, { shouldDirty: true })}
                        size="sm"
                      />
                      <span className="text-sm text-gray-700">Permitir cerrar</span>
                    </div>
                    <Input
                      size="sm"
                      label="Mínimo antes de cerrar"
                      type="number"
                      append="segundos"
                      min={1}
                      max={120}
                      disabled={!videoDismissible}
                      error={errors.videoMinSecondsBeforeDismiss?.message}
                      {...register('videoMinSecondsBeforeDismiss', { valueAsNumber: true })}
                    />
                  </div>
                </div>
              )}

              {adType == 'BANNER' && (
                <div className="space-y-4">
                  <input
                    ref={imageFileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (!file) return;
                      handleBannerFile(file);
                      event.target.value = '';
                    }}
                  />
                  <div
                    role="button"
                    tabIndex={0}
                    className={`w-full border-2 border-dashed p-4 transition ${
                      isDraggingImage
                        ? 'border-[color:var(--brand-primary)] bg-gray-50'
                        : 'border-gray-300 bg-white'
                    }`}
                    onClick={() => imageFileInputRef.current?.click()}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        imageFileInputRef.current?.click();
                      }
                    }}
                    onDragOver={(event) => {
                      event.preventDefault();
                      setIsDraggingImage(true);
                    }}
                    onDragLeave={() => setIsDraggingImage(false)}
                    onDrop={(event) => {
                      event.preventDefault();
                      setIsDraggingImage(false);
                      const file = event.dataTransfer.files?.[0];
                      handleBannerFile(file);
                    }}
                  >
                    <div className="flex flex-col gap-3">
                    <div>
                      <p className="text-sm font-medium text-gray-800">Imagen</p>
                      <p className="text-xs text-gray-500">Haz clic o arrastra la imagen aquí.</p>
                    </div>

                    <div className="space-y-3 border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600">
                      <p className="font-semibold text-gray-700">{creativeLabels.imageRecommendations.title}</p>
                      <div className="space-y-1">
                        <p className="font-medium text-gray-700">{creativeLabels.imageRecommendations.bannerTitle}</p>
                        <p>{creativeLabels.imageRecommendations.bannerSize}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="font-medium text-gray-700">{creativeLabels.imageRecommendations.landscapeTitle}</p>
                        <p>{creativeLabels.imageRecommendations.landscapeSize}</p>
                      </div>
                      <ul className="list-disc space-y-1 pl-4">
                        <li>{creativeLabels.imageRecommendations.safeArea}</li>
                        <li>{creativeLabels.imageRecommendations.formats}</li>
                        <li>{creativeLabels.imageRecommendations.separate}</li>
                      </ul>
                    </div>

                      {bannerImageUrl ? (
                        <div className="flex flex-col gap-2">
                          <div className="text-xs text-gray-500 flex items-center">
                            <div>Imagen cargada</div>
                            <CheckCircle className="h-4 text-green-600" />
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              loading={uploadingImage}
                              onClick={(event) => {
                                event.stopPropagation();
                                imageFileInputRef.current?.click();
                              }}
                            >
                              {uploadingImage ? `Subiendo ${uploadProgress || 0}%` : 'Reemplazar imagen'}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Button type="button" variant="outline" size="sm" loading={uploadingImage}>
                            {uploadingImage ? `Subiendo ${uploadProgress || 0}%` : 'Subir imagen'}
                          </Button>
                        </div>
                      )}

                      {uploadingImage && (
                        <div className="w-full h-1 bg-gray-200 overflow-hidden">
                          <div
                            className="h-full bg-[color:var(--brand-primary)] transition-all duration-200"
                            style={{ width: `${uploadProgress}%` }}
                          />
                        </div>
                      )}
                      {errors.bannerImageUrl?.message && (
                        <p className="text-xs text-red-600">{errors.bannerImageUrl?.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Select
                      controllerSize="sm"
                      label="Posición de la imagen"
                      value={bannerPosition}
                      onChange={(e) => setValue('bannerPosition', e.target.value as BannerOverlayPosition)}
                      options={[
                        { value: 'TOP', label: 'Arriba' },
                        { value: 'BOTTOM', label: 'Abajo' },
                      ]}
                    />
                    <Input
                      size="sm"
                      label="Escala de la imagen"
                      type="number"
                      min={0.3}
                      max={3}
                      step={0.1}
                      error={errors.bannerScaleFactor?.message}
                      {...register('bannerScaleFactor', { valueAsNumber: true })}
                    />
                    <Input
                      size="sm"
                      label="Duración en pantalla"
                      type="number"
                      append="segundos"
                      min={1}
                      error={errors.showTimeInSeconds?.message}
                      {...register('showTimeInSeconds', { valueAsNumber: true })}
                    />
                  </div>
                </div>
              )}

              {adType == 'TEXT' && (
                <div className="space-y-4">
                  <style>
                    {`
                      input.color-input::-webkit-color-swatch-wrapper { padding: 0; }
                      input.color-input::-webkit-color-swatch { border: none; border-radius: 6px; }
                      input.color-input::-moz-color-swatch { border: none; border-radius: 6px; }
                    `}
                  </style>
                  <RichMessageEditor
                    defaultStyle={{ fontFamily: watch('textFontFamily'), fontSize: watch('textFontSize'), color: watch('textFontColor') }}
                    backgroundColor={watch('textBackgroundColor') || '#111827'}
                    onUploadingChange={setUploadingImage}
                    items={watch('textItems')?.length ? watch('textItems')! : [{ kind: 'TEXT', text: textValue }]}
                    error={errors.text?.message}
                    onChange={items => {
                      setValue('textItems', items, { shouldDirty: true });
                      setValue('text', messageFallback(items), { shouldDirty: true, shouldValidate: true });
                    }}
                    onPreset={preset => {
                      setValue('textFontColor', preset === 'promotion' ? '#facc15' : '#ffffff', { shouldDirty: true });
                      setValue('textBackgroundColor', preset === 'celebration' ? '#6d28d9' : '#111827', { shouldDirty: true });
                      setValue('textFontSize', preset === 'simple' ? 24 : 32, { shouldDirty: true });
                      setValue('textTickerSpeedSeconds', 12, { shouldDirty: true });
                      if (!textValue.trim()) {
                        const items: RichTextItem[] = preset === 'simple' ? [{ kind: 'TEXT', text: homeTranslations.richMessage.sample }]
                          : [{ kind: 'STICKER', sticker: preset === 'promotion' ? 'gift' : 'party' }, { kind: 'TEXT', text: homeTranslations.richMessage.sample }];
                        setValue('textItems', items, { shouldDirty: true });
                        setValue('text', messageFallback(items), { shouldDirty: true });
                      }
                    }}
                  />
                  <div className="grid grid-cols-1 md:grid-cols-3 items-start gap-4">
                    <div>
                      <label htmlFor="text-background-color" className="mb-1 block text-sm font-medium text-gray-700">{homeTranslations.richMessage.backgroundColor}</label>
                      <div role="group" aria-label={homeTranslations.richMessage.backgroundColor} className="flex h-10 rounded-md shadow-sm">
                        <Controller name="textBackgroundColor" control={form.control} render={({ field }) => (
                          <input id="text-background-color" type="color"
                            className="color-input h-10 min-w-0 flex-1 cursor-pointer rounded-l-md border border-r-0 border-gray-300 p-1 disabled:cursor-default disabled:opacity-40"
                            value={isTransparentBackground ? lastNonTransparentBgRef.current : (field.value || '#111827')}
                            onClick={() => {
                              if (isTransparentBackground) {
                                setValue('textBackgroundColor', lastNonTransparentBgRef.current, { shouldDirty: true, shouldValidate: true });
                              }
                            }}
                            onChange={event => field.onChange(event.target.value)} />
                        )} />
                        <label
                          className={`flex cursor-pointer items-center gap-2 rounded-r-md border px-3 text-sm focus-within:z-10 focus-within:ring-2 focus-within:ring-indigo-500 ${isTransparentBackground ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}>
                          <input type="checkbox" checked={isTransparentBackground}
                            className="h-4 w-4 cursor-pointer accent-indigo-600"
                            onChange={event => setValue('textBackgroundColor', event.target.checked ? 'transparent' : lastNonTransparentBgRef.current, { shouldDirty: true, shouldValidate: true })} />
                          {homeTranslations.richMessage.transparent}
                        </label>
                      </div>
                      {errors.textBackgroundColor?.message && <p className="mt-1 text-xs text-red-600">{errors.textBackgroundColor.message}</p>}
                    </div>
                    <Input
                      size="sm"
                      label="Velocidad"
                      type="number"
                      min={4}
                      max={60}
                      append="s"
                      error={errors.textTickerSpeedSeconds?.message}
                      {...register('textTickerSpeedSeconds', { valueAsNumber: true })}
                    />
                    <Select
                      controllerSize="sm"
                      label="Posición del ticker"
                      value={textPosition}
                      onChange={(e) => setValue('bannerPosition', e.target.value as BannerOverlayPosition)}
                      options={[
                        { value: 'TOP', label: 'Arriba' },
                        { value: 'BOTTOM', label: 'Abajo' },
                      ]}
                    />

                  </div>
                </div>
              )}

              {adType === 'NEWS_TICKER' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Select
                      controllerSize="sm"
                      label="Fuente de noticias"
                      value={newsTickerSourceType}
                      onChange={(event) =>
                        setValue('newsTickerSourceType', event.target.value as NewsTickerSourceType, {
                          shouldDirty: true,
                          shouldValidate: true,
                        })
                      }
                      options={[
                        { value: 'MANUAL', label: 'Manual' },
                        { value: 'RSS', label: 'RSS' },
                        { value: 'MANUAL_AND_RSS', label: 'Manual + RSS' },
                      ]}
                    />
                    <Input
                      size="sm"
                      label="Máximo de noticias"
                      type="number"
                      min={1}
                      max={30}
                      error={errors.newsTickerMaxItems?.message}
                      {...register('newsTickerMaxItems', { valueAsNumber: true })}
                    />
                    <Input
                      size="sm"
                      label="Separador"
                      type="text"
                      error={errors.newsTickerSeparator?.message}
                      {...register('newsTickerSeparator')}
                    />
                  </div>

                  {(newsTickerSourceType === 'MANUAL' || newsTickerSourceType === 'MANUAL_AND_RSS') && (
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700">Noticias manuales</label>
                      <textarea
                        className={`min-h-28 w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                          errors.newsTickerItemsText?.message ? 'border-red-300' : 'border-gray-300'
                        }`}
                        placeholder="Una noticia por línea"
                        {...register('newsTickerItemsText')}
                      />
                      {errors.newsTickerItemsText?.message && (
                        <p className="text-xs text-red-600">{errors.newsTickerItemsText.message}</p>
                      )}
                    </div>
                  )}

                  {(newsTickerSourceType === 'RSS' || newsTickerSourceType === 'MANUAL_AND_RSS') && (
                    <Input
                      size="sm"
                      label="URL RSS"
                      type="url"
                      placeholder="https://example.com/rss"
                      error={errors.newsTickerRssUrl?.message}
                      {...register('newsTickerRssUrl')}
                    />
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Controller
                      name="newsTickerLeftImageUrl"
                      control={form.control}
                      render={({ field }) => (
                        <ImageUrlUploadField
                          size="sm"
                          label="Imagen izquierda"
                          placeholder="https://..."
                          value={field.value || ''}
                          onChange={field.onChange}
                          source={MediaSource.AD_IMAGE}
                          error={errors.newsTickerLeftImageUrl?.message}
                          disabled={isSubmitting || createMutation.isPending || updateMutation.isPending || uploadingImage || uploadingVideo}
                          showPreview={false}
                          showInlinePreview
                        />
                      )}
                    />
                    <Controller
                      name="newsTickerBackgroundImageUrl"
                      control={form.control}
                      render={({ field }) => (
                        <ImageUrlUploadField
                          size="sm"
                          label="Imagen de fondo"
                          placeholder="https://..."
                          value={field.value || ''}
                          onChange={field.onChange}
                          source={MediaSource.AD_IMAGE}
                          error={errors.newsTickerBackgroundImageUrl?.message}
                          disabled={isSubmitting || createMutation.isPending || updateMutation.isPending || uploadingImage || uploadingVideo}
                          showPreview={false}
                          showInlinePreview
                        />
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {fontSelector}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Input
                      size="sm"
                      label="Tamaño de fuente"
                      type="number"
                      min={10}
                      max={200}
                      append="px"
                      error={errors.textFontSize?.message}
                      {...register('textFontSize', { valueAsNumber: true })}
                    />
                    <Controller
                      name="textFontColor"
                      control={form.control}
                      render={({ field }) => (
                        <Input
                          size="sm"
                          label="Color de fuente"
                          type="color"
                          error={errors.textFontColor?.message}
                          className="h-10 p-0 color-input"
                          value={field.value || '#ffffff'}
                          onChange={(event) => field.onChange(event.target.value)}
                          style={{ backgroundColor: field.value || '#ffffff' }}
                        />
                      )}
                    />
                    <Controller
                      name="textBackgroundColor"
                      control={form.control}
                      render={({ field }) => (
                        <Input
                          size="sm"
                          label="Color de fondo"
                          type="color"
                          error={errors.textBackgroundColor?.message}
                          className="h-10 p-0 color-input"
                          value={field.value === 'transparent' ? '#111827' : (field.value || '#111827')}
                          onChange={(event) => field.onChange(event.target.value)}
                          style={{ backgroundColor: field.value === 'transparent' ? '#111827' : (field.value || '#111827') }}
                        />
                      )}
                    />
                    <Input
                      size="sm"
                      label="Velocidad"
                      type="number"
                      min={4}
                      max={60}
                      append="s"
                      error={errors.textTickerSpeedSeconds?.message}
                      {...register('textTickerSpeedSeconds', { valueAsNumber: true })}
                    />
                  </div>

                  <div className="flex items-center gap-3">
                    <Toggle
                      checked={watch('newsTickerLoopEnabled') ?? true}
                      onChange={(value) =>
                        setValue('newsTickerLoopEnabled', value, {
                          shouldDirty: true,
                          shouldValidate: true,
                        })
                      }
                      size="sm"
                    />
                    <span className="text-sm text-gray-700">Repetir noticias mientras dure el anuncio</span>
                  </div>
                </div>
              )}
            </section>

            <section className="space-y-4 p-4 sm:p-5">
              <div>
                <h2 className="text-sm font-semibold text-gray-900">{creativeLabels.location}</h2>
                <p className="text-xs text-gray-500">Selecciona en qué pantallas se verá el anuncio.</p>
              </div>

              {adType === 'VIDEO' ? (
                <div className="space-y-4">
                  <div className="border border-gray-200 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900">TV en vivo</p>
                        <p className="text-xs text-gray-500">Permite reproducir anuncios de video durante la transmisión en vivo, <span className="text-red-500">pero solo con horarios recurrentes.</span></p>
                      </div>
                      <LocationToggle
                        disabledReason={creativeLabels.enableLocation}
                        disabled={!campaignLocations.live}
                        checked={showOnLiveTv}
                        onChange={(v) => {
                          setValue('showOnLiveTv', v, { shouldDirty: true, shouldValidate: true });

                        }}
                        size="sm"
                      />
                    </div>


                  </div>

                  <div className="border border-gray-200 p-4 space-y-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900">VOD (Películas y Series)</p>
                        <p className="text-xs text-gray-500">Se muestra antes del contenido bajo demanda.</p>
                      </div>
                      <LocationToggle
                        disabledReason={creativeLabels.enableLocation}
                        disabled={!campaignLocations.vod}
                        checked={showOnVod}
                        onChange={(v) => {
                          setValue('showOnVod', v, { shouldDirty: true, shouldValidate: true });
                        }}
                        size="sm"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="border border-gray-200 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900">TV en vivo</p>
                        <p className="text-xs text-gray-500">Se muestra durante la transmisión en vivo.</p>
                      </div>
                      <LocationToggle
                        disabledReason={creativeLabels.enableLocation}
                        disabled={!campaignLocations.live}
                        checked={showOnLiveTv}
                        onChange={(v) => {
                          setValue('showOnLiveTv', v, { shouldDirty: true, shouldValidate: true });

                        }}
                        size="sm"
                      />
                    </div>


                  </div>

                  <div className="border border-gray-200 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900">VOD (Películas y Series)</p>
                        <p className="text-xs text-gray-500">Se muestra dentro del catálogo bajo demanda.</p>
                      </div>
                      <LocationToggle
                        disabledReason={creativeLabels.enableLocation}
                        disabled={!campaignLocations.vod}
                        checked={showOnVod}
                        onChange={(v) => {
                          setValue('showOnVod', v, { shouldDirty: true, shouldValidate: true });
                        }}
                        size="sm"
                      />
                    </div>
                  </div>
                </div>
              )}
              <fieldset disabled={!campaignLocations.home}>
              <HomeCampaignFields disabledReason={!campaignLocations.home ? creativeLabels.enableLocation : undefined} readOnly={!campaignLocations.home} useCreativeAsset value={watch('home')} type={adType}
                playbackAsset={adType === 'VIDEO' ? videoUrl : bannerImageUrl}
                onChange={value => setValue('home', value, { shouldDirty: true, shouldValidate: true })}
                error={errors.home ? (errors.home.mediaUrl ? homeLabels.invalidMedia : homeLabels.invalidSettings) : undefined} />
              </fieldset>
              <fieldset disabled={!campaignLocations.radio}>
              <RadioCampaignFields disabledReason={creativeLabels.enableLocation} disabled={!campaignLocations.radio} hideTargeting
                type={adType}
                enabled={watch('showOnRadio')}
                mode={watch('radioDisplayMode')}
                included={watch('includeRadioStationIds')}
                excluded={watch('excludeRadioStationIds')}
                onEnabledChange={(value) => campaignLocations.radio && setValue('showOnRadio', value, { shouldDirty: true, shouldValidate: true })}
                onModeChange={(value) => setValue('radioDisplayMode', value, { shouldDirty: true, shouldValidate: true })}
                onIncludedChange={(value) => setValue('includeRadioStationIds', value, { shouldDirty: true, shouldValidate: true })}
                onExcludedChange={(value) => setValue('excludeRadioStationIds', value, { shouldDirty: true, shouldValidate: true })}
              />
              </fieldset>
            </section>

            </div>}
            </div>
            <div id="campaign-settings-panel" role="tabpanel" aria-labelledby="campaign-editor-tab-campaign" hidden={editorSection !== 'campaign'} className="space-y-5">
              <section className="space-y-3">
                <h2 className="text-sm font-semibold text-gray-900">{creativeLabels.targeting}</h2>

                <div className="border border-gray-200 p-4 space-y-4">
                  <div className="flex items-center justify-between gap-3">
                    <LocationHelpLabel label={creativeLabels.live} description={creativeLabels.locationHelp.live} />
                    <Toggle aria-label={creativeLabels.live} size="sm" checked={campaignLocations.live} onChange={value => changeCampaignLocation('live', value)} />
                  </div>
                      {campaignLocations.live && <div className="space-y-4">
                        <Select
                          controllerSize="sm"
                          label="Canales de TV en vivo"
                          value={channelMode}
                          onChange={(event) => updateChannelMode(event.target.value as ChannelMode)}
                          options={[
                            { value: 'ALL', label: 'Todos los canales' },
                            { value: 'INCLUDE', label: 'Solo estos canales' },
                            { value: 'EXCLUDE', label: 'Excluir estos canales' },
                          ]}
                          helperText={
                            channelMode === 'ALL'
                              ? 'Se mostrará en todos los canales.'
                              : channelMode === 'INCLUDE'
                                ? 'Solo en los canales seleccionados.'
                                : 'En todos excepto los seleccionados.'
                          }
                        />
                        {channelMode === 'INCLUDE' && (
                          <Controller
                            name="includeChannelIds"
                            control={form.control}
                            render={({ field }) => (
                              <MultiSelect
                                label="Selecciona los canales"
                                options={channelOptions}
                                value={field.value || []}
                                onChange={field.onChange}
                                error={errors?.includeChannelIds?.message as string | undefined}
                              />
                            )}
                          />
                        )}

                        {channelMode === 'EXCLUDE' && (
                          <Controller
                            name="excludeChannelIds"
                            control={form.control}
                            render={({ field }) => (
                              <MultiSelect
                                label="Selecciona los canales"
                                options={channelOptions}
                                value={field.value || []}
                                onChange={field.onChange}
                                error={errors?.excludeChannelIds?.message as string | undefined}
                              />
                            )}
                          />
                        )}
                      </div>}
                </div>
                {(['vod', 'home'] as const).map(location => <div key={location} className="flex items-center justify-between gap-3 border border-gray-200 p-4">
                  <LocationHelpLabel label={creativeLabels[location]} description={creativeLabels.locationHelp[location]} />
                  <Toggle aria-label={creativeLabels[location]} size="sm" checked={campaignLocations[location]} onChange={value => changeCampaignLocation(location, value)} />
                </div>)}
                <RadioCampaignFields collapsibleHelp type="TEXT" enabled={campaignLocations.radio} mode={watch('radioDisplayMode')}
                  onEnabledChange={value => changeCampaignLocation('radio', value)}
                  onModeChange={value => setValue('radioDisplayMode', value, { shouldDirty: true })}
                  included={watch('includeRadioStationIds')} excluded={watch('excludeRadioStationIds')}
                  onIncludedChange={value => setValue('includeRadioStationIds', value, { shouldDirty: true })}
                  onExcludedChange={value => setValue('excludeRadioStationIds', value, { shouldDirty: true })} />
              </section>


            <section className="-mx-5 space-y-4 border-t border-gray-200 px-5 pt-5 sm:-mx-6 sm:px-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-sm font-semibold text-gray-900">Programación</h2>
                  <p className="text-xs text-gray-500">
                    Define el periodo general y, si aplica, horarios recurrentes para disparar la campaña.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addSchedule}
                  disabled={isSubmitting || createMutation.isPending || updateMutation.isPending || uploadingImage || uploadingVideo}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Agregar horario
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Select
                  controllerSize="sm"
                  label="Vigencia"
                  value={durationMode}
                  onChange={(event) => {
                    setValue('durationMode', event.target.value as AdCampaignDurationMode, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  }}
                  options={[
                    { value: 'DATE_RANGE', label: 'Rango de fechas' },
                    { value: 'UNTIL_DEACTIVATED', label: 'Hasta desactivar' },
                  ]}
                />
                <Input
                  size="sm"
                  label="Inicio"
                  type="datetime-local"
                  disabled={durationMode === 'UNTIL_DEACTIVATED'}
                  error={errors.startAt?.message}
                  {...register('startAt')}
                />
                <Input
                  size="sm"
                  label="Fin"
                  type="datetime-local"
                  disabled={durationMode === 'UNTIL_DEACTIVATED'}
                  error={errors.endAt?.message}
                  {...register('endAt')}
                />
              </div>

              {schedules.length === 0 ? (
                <div className="border border-dashed border-gray-200 bg-gray-50 p-3 text-xs text-gray-500">
                  Sin horarios recurrentes. La campaña seguirá usando solo las fechas generales de inicio y fin.
                </div>
              ) : (
                <div className="space-y-4">
                  {schedules.map((schedule, index) => {
                    const scheduleError = errors.schedules?.[index];
                    return (
                      <div key={`schedule-${index}`} className="border border-gray-200 p-4 space-y-4">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="text-sm font-medium text-gray-900">Horario {index + 1}</p>
                            <p className="text-xs text-gray-500">Selecciona los días y la hora exacta local en que debe dispararse.</p>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => removeSchedule(index)}
                            disabled={isSubmitting || createMutation.isPending || updateMutation.isPending || uploadingImage || uploadingVideo}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Quitar
                          </Button>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_180px] gap-4 items-start">
                          <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">Días</label>
                            <div className="flex flex-wrap gap-2">
                              {scheduleDayOptions.map((day) => {
                                const active = schedule.daysOfWeek.includes(day.value);
                                return (
                                  <button
                                    key={`${index}-${day.value}`}
                                    type="button"
                                    onClick={() => toggleScheduleDay(index, day.value)}
                                    className={`inline-flex h-9 min-w-9 items-center justify-center rounded-md border px-3 text-sm font-medium transition ${
                                      active
                                        ? 'border-[color:var(--brand-primary)] bg-[color:var(--brand-primary)] text-white'
                                        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                                    }`}
                                    aria-pressed={active}
                                    title={day.label}
                                  >
                                    {day.shortLabel}
                                  </button>
                                );
                              })}
                            </div>
                            {scheduleError?.daysOfWeek?.message && (
                              <p className="text-xs text-red-600">{scheduleError.daysOfWeek.message}</p>
                            )}
                          </div>

                          <Input
                            size="sm"
                            label="Hora"
                            type="time"
                            step={1}
                            value={schedule.triggerTime}
                            onChange={(event) =>
                              updateSchedule(index, { ...schedule, triggerTime: event.target.value })
                            }
                            error={scheduleError?.triggerTime?.message}
                          />
                        </div>
                        <p className="text-xs text-gray-500">
                          Ejemplo: Lun-Vie a las 20:00:15 significa que el anuncio debe dispararse exactamente a las 8:00:15 PM hora local.
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            <section className="-mx-5 space-y-4 border-t border-gray-200 px-5 pt-5 sm:-mx-6 sm:px-6">
              <div>
                <h2 className="text-sm font-semibold text-gray-900">Límites y prioridad</h2>
                <p className="text-xs text-gray-500">Define prioridad, peso y límites de impresiones.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input
                  size="sm"
                  label="Prioridad"
                  type="number"
                  min={0}
                  max={100}
                  helperText={priorityHelperText}
                  error={errors?.priority?.message}
                  {...priorityRegister}
                />
                <Input
                  size="sm"
                  label="Peso"
                  type="number"
                  min={1}
                  max={100}
                  helperText={weightHelperText}
                  error={errors?.weight?.message}
                  {...weightRegister}
                  onBlur={(event) => {
                    weightRegister.onBlur(event);
                    const next = Number(event.target.value);
                    if (!Number.isNaN(next) && next <= 0) {
                      setValue('weight', 1, { shouldDirty: true, shouldValidate: true });
                    }
                  }}
                />
                <Controller
                  name="maxImpressionsPerUserPerDay"
                  control={form.control}
                  render={({ field }) => {
                    const isNoLimit = maxImpressionsPerUserPerDay === null;
                    return (
                      <div>
                        <Input
                          size="sm"
                          label="Máx. impresiones por usuario/día"
                          type="number"
                          min={0}
                          helperText="Cuántas veces como máximo se mostrará este anuncio a un mismo usuario en un día."
                          error={errors?.maxImpressionsPerUserPerDay?.message}
                          disabled={isNoLimit}
                          value={field.value ?? ''}
                          onChange={(event) => {
                            const nextValue = event.target.value;
                            if (nextValue === '') {
                              field.onChange(null);
                              return;
                            }
                            const parsed = Number(nextValue);
                            field.onChange(Number.isNaN(parsed) ? null : parsed);
                          }}
                          onBlur={() => {
                            field.onBlur();
                            if (!isNoLimit && (field.value === null || Number.isNaN(field.value))) {
                              setValue('maxImpressionsPerUserPerDay', lastMaxImpressionsRef.current || 0, {
                                shouldDirty: true,
                                shouldValidate: true,
                              });
                            }
                          }}
                        />
                        <Toggle
                          className="mt-2"
                          checked={isNoLimit}
                          label="No limitar"
                          onChange={(checked) => {
                            if (checked) {
                              setValue('maxImpressionsPerUserPerDay', null, {
                                shouldDirty: true,
                                shouldValidate: true,
                              });
                            } else {
                              setValue('maxImpressionsPerUserPerDay', lastMaxImpressionsRef.current || 0, {
                                shouldDirty: true,
                                shouldValidate: true,
                              });
                            }
                          }}
                        />
                      </div>
                    );
                  }}
                />
              </div>
            </section>

            </div>
            {audienceEnabled && <section id="campaign-segmentation-panel" role="tabpanel" aria-labelledby="campaign-editor-tab-segmentation" hidden={editorSection !== 'segmentation'} className="space-y-6">
              <section className="space-y-4" aria-labelledby="campaign-geography-heading">
                <div className="grid gap-4 sm:grid-cols-2">
                  <LocationTargetSelector title={homeTranslations.audience.includeLocations} help={homeTranslations.audience.includeHelp}
                    value={locationTargets.filter(target => !target.excluded)}
                    onChange={targets => setValue('locationTargets', [...targets.map(target => ({ ...target, excluded: false })), ...locationTargets.filter(target => target.excluded)], { shouldDirty: true })}/>
                  <LocationTargetSelector title={homeTranslations.audience.excludeLocations} help={homeTranslations.audience.excludeHelp}
                    value={locationTargets.filter(target => target.excluded)}
                    onChange={targets => setValue('locationTargets', [...locationTargets.filter(target => !target.excluded), ...targets.map(target => ({ ...target, excluded: true }))], { shouldDirty: true })}/>
                </div>
              </section>
              <section className="space-y-4 border-t border-gray-200 pt-5" aria-labelledby="campaign-groups-heading">
                <CampaignAudienceFields value={watch('audience') ?? {}}
                  onChange={value => setValue('audience', value, { shouldDirty: true })}/>
              </section>
            </section>}
            </div>
          </form>
          </div>
          <div className="min-w-0 max-h-[30%] shrink-0 overflow-y-auto xl:col-start-1 xl:row-start-1 xl:max-h-full xl:w-full xl:max-w-[380px] xl:justify-self-end">
          {editorSection === 'creatives' && creatives[selectedCreative] ? <CampaignPreviewPanel
            adType={adType}
            durationMode={durationMode}
            startAt={startAtValue}
            endAt={endAtValue}
            scheduleCount={schedules.length}
            bannerImageUrl={bannerImageUrl}
            bannerPosition={bannerPosition}
            bannerScaleFactor={bannerScaleFactor}
            textValue={textValue}
            textItems={watch('textItems')}
            textFontSize={textFontSize}
            textFontColor={textFontColor}
            textFontFamily={textFontFamily}
            textBackgroundColor={textBackgroundColor}
            textTickerSpeedSeconds={textTickerSpeedSeconds}
            textPosition={textPosition}
            videoUrl={videoUrl}
            showTimeInSeconds={showTimeInSeconds}
          /> : <aside className="self-start border border-gray-200 bg-white shadow-sm">
            <h2 className="border-b border-gray-100 px-4 py-3 text-sm font-semibold text-gray-900">
              {editorSection !== 'creatives' ? creativeLabels.campaignPreview : creativeLabels.preview}
            </h2>
            <div className="space-y-4 p-4">
              <div className="flex aspect-video items-center justify-center border border-gray-200 bg-slate-950 p-6 text-center text-sm text-slate-300">
                {editorSection !== 'creatives' ? <div className="space-y-3">
                  <p className="text-lg font-semibold text-white">{watch('name') || creativeLabels.unnamedCampaign}</p>
                  <p>{creatives.length} {creativeLabels.title.toLowerCase()}</p>
                  <p>{(Object.keys(campaignLocations) as Array<keyof typeof campaignLocations>).filter(location => campaignLocations[location]).map(location => creativeLabels[location]).join(' · ') || creativeLabels.noLocations}</p>
                </div> : <p>{creativeLabels.choose}</p>}
              </div>
              {editorSection !== 'creatives' && <div className="space-y-3 text-sm">
                <div className="border border-gray-200 p-3">
                  <p className="mb-1 text-xs text-gray-500">{creativeLabels.schedule}</p>
                  <p>{durationMode === 'UNTIL_DEACTIVATED' ? creativeLabels.untilDisabled : `${startAtValue.replace('T', ' ') || '—'} → ${endAtValue.replace('T', ' ') || '—'}`}</p>
                  <p className="mt-1 text-xs text-gray-500">{creativeLabels.recurringSchedules}: {schedules.length}</p>
                </div>
                <div className="flex justify-between gap-3 bg-gray-50 p-3"><span>{creativeLabels.priority}</span><span>{watch('priority')}</span></div>
                <div className="flex justify-between gap-3 bg-gray-50 p-3"><span>{creativeLabels.dailyLimit}</span><span>{watch('maxImpressionsPerUserPerDay') || creativeLabels.unlimited}</span></div>
                {audienceEnabled && <CampaignSegmentationSummary locations={locationTargets} audience={watch('audience') ?? {}} />}

              </div>}
            </div>
          </aside>}
          </div>
        </div>
      </div>
    </div>
);
};

export default CampaignForm;
